import React from "react";

import { twJoin } from "tailwind-merge";

import { Pill } from "src/design-system/Pill";
import { ModelIcon } from "src/genai/ModelSelector/ModelIcon";
import { ResidencyBadges } from "src/genai/ModelSelector/ModelResidencyBadges";
import { ModelOption } from "src/genai/ModelSelector/types";

type ModelItemProps = {
  model: ModelOption;
  /** Dim the name for older (superseded/deprecated) models. */
  dimmed?: boolean;
  /**
   * Render the right-aligned hosting/region badges (default true). The Compare
   * modal turns this off — it has a dedicated Residency column.
   */
  showResidency?: boolean;
  /** Tag the model "Outdated" next to its name (used in the Compare modal). */
  outdated?: boolean;
  dataLoc?: string;
};

/**
 * A model's row: provider mark + name + the right-aligned residency badges (a
 * BYOK deployment's credential name shows in its badge). Shared by the dropdown
 * rows, the trigger, and the Compare modal's model column.
 */
export const ModelItem: React.FC<ModelItemProps> = ({
  model,
  dimmed = false,
  showResidency = true,
  outdated = false,
  dataLoc,
}) => {
  const nameLabel = (
    <span
      className={twJoin(
        "min-w-0 truncate font-inter-medium-13px",
        dimmed ? "text-gray-600" : "text-gray-800",
      )}
    >
      {model.displayName}
    </span>
  );

  return (
    <span
      className="flex min-w-0 items-center gap-2.5 overflow-hidden"
      data-loc={dataLoc}
    >
      <span className="shrink-0">
        <ModelIcon
          icon={model.icon}
          iconUrl={model.iconUrl}
          modelName={model.displayName}
          provider={model.provider}
        />
      </span>
      {nameLabel}
      {outdated && (
        <span className="shrink-0">
          <Pill size="sm" variant="gray">
            <Pill.Text>Outdated</Pill.Text>
          </Pill>
        </span>
      )}
      {showResidency && (
        <span className="ml-auto flex shrink-0 items-center gap-1.5 pl-2">
          <ResidencyBadges model={model} />
        </span>
      )}
    </span>
  );
};
